import { LiveFeedRepository } from "../../../domain/live-feed-repository";
import { Injectable } from "@angular/core";
import {
  LiveFeed,
  LiveFeedInfo,
  LiveFeedStreamingPlatform,
  StreamingPlatform,
  StreamSourceSettings,
} from "../../../domain/live-feed";
import { Subject } from "rxjs";
import { HttpService } from "../../http/http";
import { UserService } from "../../../domain/user-service";

@Injectable()
export class LiveFeedRepositoryImpl implements LiveFeedRepository {
  liveFeedCreated: Subject<LiveFeedInfo> = new Subject<LiveFeedInfo>();
  liveFeedUpdated: Subject<LiveFeedInfo> = new Subject<LiveFeedInfo>();

  constructor(
    private httpService: HttpService,
    private userService: UserService,
  ) {}

  getLiveFeeds(nameFilter?: string) {
    const filters = [];
    if (nameFilter && nameFilter.length > 0) {
      filters.push("NameFilter=" + nameFilter);
    }
    return this.httpService
      .get(`livefeeds${filters.length > 0 ? "?" + filters.join("&") : ""}`)
      .then((response) =>
        response.map((x) => {
          return new LiveFeed(x.Id, x.Name, x.TenantUniqueId, x.Thumbnail);
        }),
      );
  }

  isStreamIdAvailable(streamId) {
    return this.httpService.get(`livefeeds/isStreamIdAvailable/${streamId}`);
  }

  isFeedIdAvailable(feedId: string) {
    return this.httpService.get(`livefeeds/isFeedIdAvailable/${feedId}`);
  }

  updateLiveFeed(liveFeed: LiveFeedInfo) {
    return this.httpService
      .put(`livefeeds/${liveFeed.id}`, this.initCreateUpdateRequest(liveFeed))
      .then((createLiveFeedRes) => {
        this.liveFeedUpdated.next(liveFeed);
        return createLiveFeedRes;
      });
  }

  createLiveFeed(liveFeed: LiveFeedInfo) {
    return this.httpService
      .post("livefeeds", this.initCreateUpdateRequest(liveFeed))
      .then((createLiveFeedRes) => {
        liveFeed.id = createLiveFeedRes.Id;
        this.liveFeedCreated.next(liveFeed);
        return createLiveFeedRes;
      });
  }

  getLiveFeedInfo(id: string) {
    return this.httpService.get(`livefeeds/${id}`).then((x) => {
      const platformsDictionary = x.Platforms;
      const streamingPlatforms = Object.keys(platformsDictionary).map(
        (platformId) => {
          const p = platformsDictionary[platformId];
          const s = p.StreamSourceSettings;
          const streamSourceSettings = new StreamSourceSettings(
            s.SourceType,
            s.EncryptionType,
            s.EncryptionKeyText,
            s.Username,
            s.Password,
            s.RemoteAddress,
            s.RemotePort,
            s.LocalPort,
            s.URL,
            s.FormattedStreamName,
            s.ZixiRemoteName,
            s.ProtocolLatencyMs,
            s.SpecialOptions,
            s.WhitelistIpRanges
              ? s.WhitelistIpRanges.split("|").filter((r) => r)
              : [],
          );
          return new LiveFeedStreamingPlatform(
            p.Id,
            p.PrivateIp,
            p.PublicIp,
            p.AvailabiliyZone.Id,
            p.AvailabiliyZone.DisplayName,
            streamSourceSettings,
          );
        },
      );
      const result = new LiveFeedInfo(
        x.Id,
        x.Name,
        x.TenantUniqueId,
        x.Thumbnail,
        this.userService.getCurrentUser().metadata.map((y) => {
          return {
            name: y.name,
            value: x.Metadata[y.name] || "",
          };
        }),
        x.TagIds,
        x.FrameFormat,
        x.InternalLatencyMs,
        x.TotalLatencyMs,
        streamingPlatforms,
      );

      return result;
    });
  }

  deleteLiveFeed(id: string) {
    return this.httpService.delete(`livefeeds/${id}`);
  }

  updateThumbnail(id: string, thumbnail: string) {
    return this.getLiveFeedInfo(id).then((liveFeedInfo) => {
      liveFeedInfo.thumbnail = thumbnail;
      return this.updateLiveFeed(liveFeedInfo);
    });
  }
  async getAllStreamingPlatforms(): Promise<StreamingPlatform[]> {
    const apiResult = await this.httpService.get(`livefeeds/platforms`);
    return apiResult.map(
      (x) =>
        new StreamingPlatform(
          x.Id,
          x.PrivateIp,
          x.PublicIp,
          x.AvailabiliyZone.Id,
          x.AvailabiliyZone.DisplayName,
        ),
    );
  }
  pingLiveFeedPreview(previewFeedId: string) {
    this.httpService.get(`livefeeds/pinglivefeedpreview/${previewFeedId}`);
  }

  /*private extractIps(availabilityZoneIps: { [az: string]: string }): { az: number, ip: string }[] {
        return Object.keys(availabilityZoneIps).map(key => {
            return {az: parseInt(key), ip: availabilityZoneIps[key]};
        });
    }*/

  private toMetadataDictionary(metadata: { name: string; value: string }[]) {
    const dictionary = {};
    metadata.forEach((x) => {
      dictionary[x.name] = x.value;
    });
    return dictionary;
  }

  private initCreateUpdateRequest(liveFeed: LiveFeedInfo) {
    const updatedMetadata = {};
    liveFeed.metadata.map((item) => {
      updatedMetadata[item.name] = item.value;
      return item;
    });
    return {
      Name: liveFeed.name,
      TenantUniqueId: liveFeed.tenantUniqueId,
      TagIds: liveFeed.tagIds,
      Metadata: updatedMetadata,
      FrameFormat: liveFeed.frameFormat,
      Thumbnail: liveFeed.thumbnail,
      TotalLatencyMs: liveFeed.totalLatencyMs,
      InternalLatencyMs: liveFeed.internalLatencyMs,
      StreamingPlatforms: liveFeed.streamingPlatforms.map((p) => ({
        StreamingPlatformId: p.id,
        SourceType: p.streamSourceSettings.sourceType,
        EncryptionType: p.streamSourceSettings.encryptionType,
        EncryptionKey: p.streamSourceSettings.encryptionKey,
        Username: p.streamSourceSettings.username,
        Password: p.streamSourceSettings.password,
        RemoteAddress: p.streamSourceSettings.remoteAddress,
        RemotePort: p.streamSourceSettings.remotePort,
        StremName: p.streamSourceSettings.streamName,
        ZixiRemoteName: p.streamSourceSettings.zixiRemoteName,
        DesiredLatencyMs: p.streamSourceSettings.protocolLatencyMs,
        StreamUrl: p.streamSourceSettings.url,
        SpecialOptions: p.streamSourceSettings.specialOptions,
        WhitelistIpRanges: p.streamSourceSettings.whitelistIpRanges?.length
          ? p.streamSourceSettings.whitelistIpRanges.join("|")
          : null,
      })),
    };
  }
}
